/**
 * 지도에 지하철을 그린다 — 노선 선, 역사 막대, 출구와 그 동선.
 *
 * 왜 우리가 그리나: 바탕 지도(protomaps)에는 철도 층이 아예 없다. 들어 있는 것은
 * 땅·물·건물·도로·경계뿐이라, 선로가 없으니 역 이름표만 얹으면 공중에 뜬 칩이 된다
 * (2026-08-10 사장님 지적). 그래서 노선 선을 우리 데이터로 들고 와 얹는다.
 *
 * 어떻게 보이나 (네이버·구글과 같은 구조):
 *   노선 색 **선**이 지나가고 → 그 위에 노선 색 **역사 막대**가 앉고 →
 *   막대에서 **동선**이 뻗어 **출구 번호**에 닿는다.
 *
 * 규칙 두 가지가 이 파일의 핵심이다.
 *  - 역사 막대의 중심과 각도는 **선 위에서** 가져온다. 역 지점은 여러 트랙 사이에 찍혀
 *    있어서 그대로 쓰면 막대가 선 옆에 떠 보인다(가산에서 8m 어긋났다). 이 계산은
 *    받는 쪽(fetch-subway-network.js)에서 이미 끝내 두었고, 여기서는 그 각도로 네 귀퉁이만 만든다.
 *  - 선은 **얇게**. 지도의 주인공은 장소 핀이고 노선은 한 겹 뒤에 있어야 한다
 *    (사장님 확정: 굵게 두고 흐리게 하는 대신 얇게 — 흐리게 하면 호선 색이 바랜다).
 *
 * 비용: 전부 GL 레이어라 지도가 알아서 잘라 그린다. 파일은 전국 한 벌을 한 번만 받고
 *       (/map/subway-*.json, 1년 캐시) 움직일 때마다 서버에 묻지 않는다.
 */

/** 승인된 굵기·크기 (2026-08-10). 숫자를 여기 모아 둔다. */
export const SUBWAY_STYLE = {
  lineWidth: 2,
  lineCasing: 3.6,
  stationHalfLenM: 105, // 승강장 210m
  stationHalfWidM: 4.5, // 폭 9m — 선(2px)보다 두툼하되 덩어리로 안 보이게
  exitRadius: 7,
  minZoomLine: 11,
  // ⚠️ 넓게 보면 13m 짜리 막대가 선보다 가늘어지고, 흰 테두리가 막대를 통째로 덮어
  //    **선이 잘린 것처럼** 보인다(2026-08-10 사장님 화면: 서울역·왕십리에서 선이 끊겼다).
  //    그래서 막대가 선보다 확실히 굵어지는 배율부터 그린다.
  minZoomStation: 15.5,
  // 이름표는 막대보다 일찍 뜬다 — 선만 있고 이름이 없으면 무슨 노선인지 알 수 없다.
  minZoomLabel: 14,
  minZoomExit: 16,
};

const SRC = {
  lines: "hotple-subway-lines",
  stations: "hotple-subway-stations",
  links: "hotple-subway-links",
  exits: "hotple-subway-exits",
};
export const SUBWAY_LAYER_IDS = [
  "hotple-subway-line-casing",
  "hotple-subway-line",
  "hotple-subway-station",
  "hotple-subway-link-casing",
  "hotple-subway-link",
  "hotple-subway-exit-dot",
  "hotple-subway-exit-num",
];

const R = Math.PI / 180;
const M_PER_LAT = 111320;
const mPerLng = (lat) => 111320 * Math.cos(lat * R);

/** 역사 막대 네 귀퉁이. 각도는 그 자리 선로의 방향이다. */
function stationRing(lng, lat, angDeg) {
  const a = angDeg * R;
  const ca = Math.cos(a);
  const sa = Math.sin(a);
  const L = SUBWAY_STYLE.stationHalfLenM;
  const W = SUBWAY_STYLE.stationHalfWidM;
  const mx = mPerLng(lat);
  const corner = (u, v) => [
    lng + (u * L * ca - v * W * sa) / mx,
    lat + (u * L * sa + v * W * ca) / M_PER_LAT,
  ];
  const ring = [corner(1, 1), corner(1, -1), corner(-1, -1), corner(-1, 1)];
  ring.push(ring[0]);
  return ring;
}

/** 점에서 역사 막대까지 가장 가까운 자리 — 동선은 여기서 뻗는다. */
function nearestOnRing(lng, lat, ring) {
  const mx = mPerLng(lat);
  const px = lng * mx;
  const py = lat * M_PER_LAT;
  let best = null;
  let bd = Infinity;
  for (let i = 0; i < ring.length - 1; i += 1) {
    const ax = ring[i][0] * mx;
    const ay = ring[i][1] * M_PER_LAT;
    const bx = ring[i + 1][0] * mx;
    const by = ring[i + 1][1] * M_PER_LAT;
    const dx = bx - ax;
    const dy = by - ay;
    const L2 = dx * dx + dy * dy;
    let t = L2 ? ((px - ax) * dx + (py - ay) * dy) / L2 : 0;
    t = Math.max(0, Math.min(1, t));
    const qx = ax + t * dx;
    const qy = ay + t * dy;
    const d = Math.hypot(px - qx, py - qy);
    if (d < bd) {
      bd = d;
      best = [qx / mx, qy / M_PER_LAT];
    }
  }
  return { point: best, d: bd };
}

/**
 * 받아 온 한 벌을 지도가 먹는 모양으로 바꾼다.
 * 파일에는 좌표와 각도만 있고, 막대·동선은 여기서 만든다(파일을 작게 두려고).
 */
export function buildSubwayGeoJson(data) {
  const lines = data?.lines || [];
  const stations = data?.stations || [];
  const exits = data?.exits || [];

  const lineFeatures = [];
  lines.forEach((line, i) => {
    // 나란히 달리는 노선은 그대로 그리면 하나가 다른 하나를 완전히 덮는다
    // (홍대입구에서 공항철도·경의중앙선이 2호선 밑에 깔렸다 — 2026-08-10 사장님 지적).
    // 노선마다 아주 작은 좌우 자리값을 줘서 넓게 볼 때 나란히 보이게 한다.
    // 많이 확대하면 0 으로 돌아온다 — 그 배율에서는 역사 막대가 선 위에 얹히므로
    // 선이 옆으로 밀려 있으면 막대와 어긋나 보인다.
    const o = (i % 5) - 2;
    (line.g || []).forEach((coords) => {
      if (!coords || coords.length < 2) return;
      lineFeatures.push({
        type: "Feature",
        properties: { c: line.c, o },
        geometry: { type: "LineString", coordinates: coords },
      });
    });
  });

  const rings = [];
  const stationFeatures = stations.map(([name, lng, lat, ang, li]) => {
    const ring = stationRing(lng, lat, ang);
    rings.push(ring);
    return {
      type: "Feature",
      properties: { c: lines[li]?.c || "#7a7a7a", n: name },
      geometry: { type: "Polygon", coordinates: [ring] },
    };
  });

  const linkFeatures = [];
  const exitFeatures = [];
  exits.forEach(([ref, lng, lat, si]) => {
    const colour = lines[stations[si]?.[4]]?.c || "#4a4550";
    const ring = rings[si];
    if (ring) {
      const near = nearestOnRing(lng, lat, ring);
      // 막대에 붙어 있는 출구는 선을 그리면 지저분하다. 떨어진 것만 잇는다.
      // ⚠️ 멀리 떨어진 출구까지 이으면 안 된다. 강남역 출구는 강남대로를 따라
      //    600m 넘게 흩어져 있어서, 다 이었더니 화면을 가로지르는 긴 줄이 생겼다
      //    (2026-08-10 후보에서 잡았다). 먼 출구는 번호만 둔다 — 역이 옆에 보이니 읽힌다.
      if (near.point && near.d > 6 && near.d < 140) {
        linkFeatures.push({
          type: "Feature",
          properties: { c: colour },
          geometry: { type: "LineString", coordinates: [near.point, [lng, lat]] },
        });
      }
    }
    exitFeatures.push({
      type: "Feature",
      properties: { c: colour, r: String(ref) },
      geometry: { type: "Point", coordinates: [lng, lat] },
    });
  });

  const fc = (features) => ({ type: "FeatureCollection", features });
  return {
    lines: fc(lineFeatures),
    stations: fc(stationFeatures),
    links: fc(linkFeatures),
    exits: fc(exitFeatures),
  };
}

/**
 * 지도에 넣는다. 이미 있으면 자료만 갈아 끼운다.
 * beforeId 를 주면 그 레이어 **밑에** 깔린다 — 장소 핀을 가리면 안 되므로 그렇게 쓴다.
 */
export function installSubwayLayers(map, data, beforeId) {
  if (!map || !data) return;
  const geo = buildSubwayGeoJson(data);

  const put = (id, collection) => {
    const existing = map.getSource(id);
    if (existing) {
      existing.setData(collection);
      return true;
    }
    map.addSource(id, { type: "geojson", data: collection });
    return false;
  };
  // ⚠️ && 로 이으면 안 된다 — 처음에는 첫 put 이 false 라 뒤의 셋이 아예 안 불린다.
  //    그래서 선만 그려지고 역·출구가 통째로 빠졌다(2026-08-10 후보에서 잡았다).
  const had = [
    put(SRC.lines, geo.lines),
    put(SRC.stations, geo.stations),
    put(SRC.links, geo.links),
    put(SRC.exits, geo.exits),
  ];
  if (had.every(Boolean) && map.getLayer(SUBWAY_LAYER_IDS[0])) return;

  const anchor = beforeId && map.getLayer(beforeId) ? beforeId : undefined;
  const add = (layer) => {
    if (map.getLayer(layer.id)) return;
    map.addLayer(layer, anchor);
  };

  add({
    id: "hotple-subway-line-casing",
    type: "line",
    source: SRC.lines,
    minzoom: SUBWAY_STYLE.minZoomLine,
    layout: { "line-cap": "round", "line-join": "round" },
    paint: {
      "line-color": "#ffffff",
      "line-width": SUBWAY_STYLE.lineCasing,
      "line-opacity": 0.9,
      "line-offset": ["interpolate", ["linear"], ["zoom"], 11, ["*", ["get", "o"], 2.2], 15.4, 0],
    },
  });
  add({
    id: "hotple-subway-line",
    type: "line",
    source: SRC.lines,
    minzoom: SUBWAY_STYLE.minZoomLine,
    layout: { "line-cap": "round", "line-join": "round" },
    paint: {
      "line-color": ["get", "c"],
      "line-width": SUBWAY_STYLE.lineWidth,
      "line-offset": ["interpolate", ["linear"], ["zoom"], 11, ["*", ["get", "o"], 2.2], 15.4, 0],
    },
  });
  add({
    id: "hotple-subway-station",
    type: "fill",
    source: SRC.stations,
    minzoom: SUBWAY_STYLE.minZoomStation,
    paint: { "fill-color": ["get", "c"], "fill-opacity": 0.9 },
  });
  add({
    id: "hotple-subway-link-casing",
    type: "line",
    source: SRC.links,
    minzoom: SUBWAY_STYLE.minZoomExit,
    layout: { "line-cap": "round" },
    paint: { "line-color": "#ffffff", "line-width": 3.2 },
  });
  add({
    id: "hotple-subway-link",
    type: "line",
    source: SRC.links,
    minzoom: SUBWAY_STYLE.minZoomExit,
    layout: { "line-cap": "round" },
    paint: { "line-color": ["get", "c"], "line-width": 1.5 },
  });
  add({
    id: "hotple-subway-exit-dot",
    type: "circle",
    source: SRC.exits,
    minzoom: SUBWAY_STYLE.minZoomExit,
    paint: {
      "circle-radius": SUBWAY_STYLE.exitRadius,
      "circle-color": ["get", "c"],
      "circle-stroke-color": "#ffffff",
      "circle-stroke-width": 1.6,
    },
  });
  add({
    id: "hotple-subway-exit-num",
    type: "symbol",
    source: SRC.exits,
    minzoom: SUBWAY_STYLE.minZoomExit,
    layout: {
      "text-field": ["get", "r"],
      "text-font": ["Noto Sans Medium"],
      "text-size": 9,
      "text-allow-overlap": true,
      "text-ignore-placement": true,
    },
    paint: { "text-color": "#ffffff" },
  });
}

/**
 * 역 이름표를 고른다 — **노선마다 하나씩**(사장님 확정 2026-08-10).
 *
 * ⚠️ 중간에 내가 임의로 두 번 손댔다가 되돌린 자리다. 같은 이름이 여러 번 뜨는 게
 *    지저분해 보여 (1) 하나만 남기고 지웠더니 경의중앙선·공항철도는 이름표가 아예
 *    없어졌고, (2) 알약 하나에 호선 동그라미를 모으는 설계를 물어보지도 않고 넣었다.
 *    사장님이 원한 것은 **노선마다 자기 이름표를 갖는 것**이다. 서울역처럼 몰린 곳에서
 *    알약이 여러 개 보이는 것은 감수한다 — 어느 막대가 무슨 역인지가 먼저다.
 *
 * 자리 규칙: 막대 **한가운데**에 얹고(따로 노는 것처럼 보이지 않게), 겹치면 버리고,
 * 아래 시트가 가리는 자리에는 그리지 않는다.
 */
export function pickStationLabels(map, data, maxY, limit = 8) {
  const stations = data?.stations || [];
  const lines = data?.lines || [];
  if (!stations.length || map.getZoom() < SUBWAY_STYLE.minZoomLabel) return [];
  const bounds = map.getBounds();
  const c = map.getCenter();
  // 자리가 모자랄 때는 화면 가운데에 가까운 역에 먼저 준다.
  const inView = stations
    .filter((st) => bounds.contains([st[1], st[2]]))
    .sort(
      (a, b) =>
        (a[1] - c.lng) ** 2 + (a[2] - c.lat) ** 2 - ((b[1] - c.lng) ** 2 + (b[2] - c.lat) ** 2)
    );
  const placed = [];
  const out = [];
  for (const st of inView) {
    if (out.length >= limit) break;
    const [name, lng, lat, , li] = st;
    const pt = map.project([lng, lat]);
    if (Number.isFinite(maxY) && pt.y > maxY) continue;
    // 사람이 부르는 이름은 "가산디지털단지역" 이다 — 받을 때 끝의 '역' 을 떼어 두었으니
    // 보여줄 때 다시 붙인다.
    const label = name.endsWith("역") ? name : name + "역";
    const halfW = 4 * label.length + 19;
    const box = { x1: pt.x - halfW, y1: pt.y - 12, x2: pt.x + halfW, y2: pt.y + 12 };
    if (placed.some((q) => !(box.x2 < q.x1 || box.x1 > q.x2 || box.y2 < q.y1 || box.y1 > q.y2))) continue;
    placed.push(box);
    out.push({
      n: label,
      lng,
      lat,
      dots: [{ c: lines[li]?.c || "#4a4550", b: lines[li]?.b || "" }],
    });
  }
  return out;
}
